import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Briefcase, MapPin, Edit, Globe, Linkedin, Instagram, Twitter, Phone, CheckCircle, Award, ExternalLink, MessageCircle, Image as ImageIcon, Star, Users, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const Profile = () => {
  const { id } = useParams();
  const { user: currentUser, token } = React.useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [pendingTestimonials, setPendingTestimonials] = useState([]);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [testimonialMessage, setTestimonialMessage] = useState('');
  const [testimonialRelationship, setTestimonialRelationship] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [businessFormData, setBusinessFormData] = useState({
    name: '',
    description: '',
    industry: '',
    website: '',
    logo: '',
    cover_image: '',
    location: '',
    team_size: '',
    business_stage: '',
    linkedin_url: '',
    instagram_url: '',
    twitter_url: '',
    phone: ''
  });
  const [formData, setFormData] = useState({
    full_name: '',
    business_name: '',
    industry: '',
    bio: '',
    profile_image: '',
    avatar_url: '',
    business_logo_url: '',
    cover_image_url: '',
    location: '',
    team_size: '',
    business_stage: '',
    website: '',
    linkedin_url: '',
    instagram_url: '',
    twitter_url: '',
    phone: '',
    cta_text: '',
    cta_url: '',
    featured_post: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  useEffect(() => {
    fetchUserProfile();
    if (!isOwnProfile) {
      fetchTestimonials();
    } else {
      fetchPendingTestimonials();
      fetchMyBusinesses();
    }
  }, [id]);

  const fetchMyBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses/my/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/member-testimonials/user/${id}`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const fetchPendingTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/member-testimonials/received`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingTestimonials(response.data.filter(t => !t.is_approved));
    } catch (error) {
      console.error('Failed to fetch pending testimonials:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${id}`);
      setUser(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        business_name: response.data.business_name || '',
        industry: response.data.industry || '',
        bio: response.data.bio || '',
        profile_image: response.data.profile_image || '',
        avatar_url: response.data.avatar_url || '',
        business_logo_url: response.data.business_logo_url || '',
        cover_image_url: response.data.cover_image_url || '',
        location: response.data.location || '',
        team_size: response.data.team_size || '',
        business_stage: response.data.business_stage || '',
        website: response.data.website || '',
        linkedin_url: response.data.linkedin_url || '',
        instagram_url: response.data.instagram_url || '',
        twitter_url: response.data.twitter_url || '',
        phone: response.data.phone || '',
        cta_text: response.data.cta_text || '',
        cta_url: response.data.cta_url || '',
        featured_post: response.data.featured_post || ''
      });
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/users/me`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully!');
      setUser(response.data);
      setIsEditing(false);
    } catch (error) {
      console.error('Profile update error:', error);
      if (error.response?.status === 413) {
        toast.error('Images are too large. Please use smaller images (under 2MB each)');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update profile');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    // Validate password length
    if (passwordData.new_password.length < 12) {
      toast.error('New password must be at least 12 characters long with uppercase, lowercase, number, and special character');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully!');
      setShowPasswordDialog(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 2MB for raw file)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB. Please compress or resize your image.');
      e.target.value = ''; // Clear the input
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (JPG, PNG, GIF, etc.)');
      e.target.value = ''; // Clear the input
      return;
    }

    try {
      toast.info('Processing image...');
      
      // Compress and convert image to base64
      const compressedBase64 = await compressImage(file, fieldName);
      
      // Check final size (base64 is ~33% larger than binary)
      const finalSizeKB = (compressedBase64.length * 0.75) / 1024;
      
      if (compressedBase64.length > 2.5 * 1024 * 1024) {
        toast.error(`Compressed image is still too large (${Math.round(finalSizeKB)}KB). Please use a smaller image.`);
        e.target.value = ''; // Clear the input
        return;
      }
      
      setFormData({ ...formData, [fieldName]: compressedBase64 });
      toast.success(`Image uploaded successfully! (${Math.round(finalSizeKB)}KB)`);
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to process image. Please try a different file.');
      e.target.value = ''; // Clear the input
    }
  };

  const compressImage = (file, fieldName) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Determine max dimensions based on field type
          let maxWidth, maxHeight, quality;
          
          if (fieldName === 'avatar_url') {
            // Avatar: smaller, square-ish
            maxWidth = 400;
            maxHeight = 400;
            quality = 0.85;
          } else if (fieldName === 'business_logo_url') {
            // Logo: medium size
            maxWidth = 600;
            maxHeight = 600;
            quality = 0.85;
          } else if (fieldName === 'cover_image_url') {
            // Cover: wider aspect ratio
            maxWidth = 1200;
            maxHeight = 400;
            quality = 0.80;
          } else {
            // Default
            maxWidth = 800;
            maxHeight = 800;
            quality = 0.85;
          }
          
          // Calculate new dimensions while maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            if (width > height) {
              width = maxWidth;
              height = width / aspectRatio;
            } else {
              height = maxHeight;
              width = height * aspectRatio;
            }
          }
          
          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleSubmitTestimonial = async () => {
    if (!testimonialMessage) {
      toast.error('Please enter a message');
      return;
    }
    
    try {
      await axios.post(`${API}/member-testimonials`, {
        to_user_id: id,
        message: testimonialMessage,
        relationship: testimonialRelationship
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial sent! Awaiting approval from recipient.');
      setShowTestimonialForm(false);
      setTestimonialMessage('');
      setTestimonialRelationship('');
    } catch (error) {
      toast.error('Failed to send testimonial');
    }
  };

  const handleApproveTestimonial = async (testimonialId, approve) => {
    try {
      await axios.put(`${API}/member-testimonials/${testimonialId}/approve?approved=${approve}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(approve ? 'Testimonial approved!' : 'Testimonial rejected');
      fetchPendingTestimonials();
      if (approve) fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  const handleBusinessDialogOpen = (business = null) => {
    if (business) {
      setEditingBusiness(business);
      setBusinessFormData({
        name: business.name,
        description: business.description,
        industry: business.industry,
        website: business.website || '',
        logo: business.logo || '',
        cover_image: business.cover_image || '',
        location: business.location || '',
        team_size: business.team_size || '',
        business_stage: business.business_stage || '',
        linkedin_url: business.linkedin_url || '',
        instagram_url: business.instagram_url || '',
        twitter_url: business.twitter_url || '',
        phone: business.phone || ''
      });
    } else {
      setEditingBusiness(null);
      setBusinessFormData({
        name: '',
        description: '',
        industry: '',
        website: '',
        logo: '',
        cover_image: '',
        location: '',
        team_size: '',
        business_stage: '',
        linkedin_url: '',
        instagram_url: '',
        twitter_url: '',
        phone: ''
      });
    }
    setBusinessDialogOpen(true);
  };

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBusiness) {
        // Update existing business
        await axios.put(`${API}/businesses/${editingBusiness.id}`, businessFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Business updated successfully!');
      } else {
        // Create new business
        await axios.post(`${API}/businesses`, businessFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Business added successfully!');
      }
      setBusinessDialogOpen(false);
      fetchMyBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save business');
    }
  };

  const handleBusinessDelete = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business deleted successfully!');
      fetchMyBusinesses();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  const handleBusinessChange = (e) => {
    setBusinessFormData({ ...businessFormData, [e.target.name]: e.target.value });
  };

  const isOwnProfile = currentUser?.id === id;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="profile-page">
        {/* Cover Image */}
        {user.cover_image_url && (
          <div className="w-full h-48 rounded-t-2xl overflow-hidden mb-4">
            <img src={user.cover_image_url} alt="Cover" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="max-w-5xl mx-auto space-y-6">
          {/* Profile Header Card */}
          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Avatar & Business Logo */}
                <div className="flex gap-4">
                  <div className="relative">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt={user.full_name} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg" />
                    ) : (
                      <div className="w-24 h-24 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white text-3xl font-bold border-4 border-white shadow-lg">
                        {user.full_name.charAt(0)}
                      </div>
                    )}
                    {user.is_email_verified && (
                      <CheckCircle className="w-6 h-6 text-blue-500 bg-white rounded-full absolute -bottom-1 -right-1" />
                    )}
                  </div>
                  {user.business_logo_url && (
                    <img src={user.business_logo_url} alt="Business Logo" className="w-24 h-24 rounded-lg object-contain border border-gray-200 p-2 bg-white" />
                  )}
                </div>

                {/* Profile Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                          {user.full_name}
                        </h1>
                        {user.is_domain_verified && (
                          <Badge className="bg-blue-100 text-blue-800">
                            <Award className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      {user.business_name && (
                        <p className="text-lg text-gray-700 font-medium">{user.business_name}</p>
                      )}
                      {user.profile_slug && (
                        <p className="text-sm text-gray-500">herenterprisehub.com/members/{user.profile_slug}</p>
                      )}
                    </div>
                    {isOwnProfile && !isEditing && (
                      <div className="flex gap-2">
                        <Button onClick={() => setIsEditing(true)} variant="outline">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Profile
                        </Button>
                        <Button onClick={() => setShowPasswordDialog(true)} variant="outline" className="bg-rose-50 hover:bg-rose-100">
                          🔒 Change Password
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Quick Facts */}
                  <div className="flex flex-wrap gap-4 mt-4">
                    {user.industry && (
                      <Badge variant="outline" className="gap-1">
                        <Briefcase className="w-3 h-3" />
                        {user.industry}
                      </Badge>
                    )}
                    {user.location && (
                      <Badge variant="outline" className="gap-1">
                        <MapPin className="w-3 h-3" />
                        {user.location}
                      </Badge>
                    )}
                    {user.team_size && (
                      <Badge variant="outline" className="gap-1">
                        <Users className="w-3 h-3" />
                        {user.team_size} team
                      </Badge>
                    )}
                    {user.business_stage && (
                      <Badge variant="outline" className="capitalize">
                        {user.business_stage}
                      </Badge>
                    )}
                  </div>

                  {/* Social Links */}
                  {(user.website || user.linkedin_url || user.instagram_url || user.twitter_url || user.phone) && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {user.website && (
                        <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:text-rose-700">
                          <Globe className="w-5 h-5" />
                        </a>
                      )}
                      {user.linkedin_url && (
                        <a href={user.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">
                          <Linkedin className="w-5 h-5" />
                        </a>
                      )}
                      {user.instagram_url && (
                        <a href={user.instagram_url} target="_blank" rel="noopener noreferrer" className="text-pink-600 hover:text-pink-700">
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {user.twitter_url && (
                        <a href={user.twitter_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-500">
                          <Twitter className="w-5 h-5" />
                        </a>
                      )}
                      {user.phone && (
                        <a href={`tel:${user.phone}`} className="text-green-600 hover:text-green-700">
                          <Phone className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Contact Information */}
                  <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-200">
                    {user.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <a href={`mailto:${user.email}`} className="hover:text-rose-600">
                          {user.email}
                        </a>
                      </div>
                    )}
                    {user.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <a href={`tel:${user.phone}`} className="hover:text-rose-600">
                          {user.phone}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  {user.cta_text && user.cta_url && (
                    <a href={user.cta_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block">
                      <Button className="bg-rose-600 hover:bg-rose-700">
                        {user.cta_text}
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              {/* Profile Completeness Meter (Own Profile Only) */}
              {isOwnProfile && user.profile_completeness !== undefined && user.profile_completeness < 100 && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Profile Completeness</span>
                    <span className="text-sm font-bold text-rose-600">{user.profile_completeness}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all"
                      style={{ width: `${user.profile_completeness}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Complete your profile to increase visibility and credibility
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Form Modal/Section */}
          {isEditing && (
            <Card className="glass">
              <CardHeader>
                <CardTitle>Edit Profile</CardTitle>
                <CardDescription>Update your basic profile information</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Basic Information</h3>
                    <Input 
                      name="full_name" 
                      placeholder="Full Name *" 
                      value={formData.full_name} 
                      onChange={handleChange}
                      required
                    />
                    <Textarea 
                      name="bio" 
                      placeholder="About / Bio" 
                      value={formData.bio} 
                      onChange={handleChange} 
                      rows={4}
                    />
                  </div>

                  {/* Avatar */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Profile Photo</h3>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Upload Profile Photo / Avatar</label>
                      <div className="flex items-center gap-4">
                        {formData.avatar_url && (
                          <img src={formData.avatar_url} alt="Avatar preview" className="w-16 h-16 rounded-full object-cover" />
                        )}
                        <Input 
                          type="file" 
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'avatar_url')}
                          className="flex-1"
                        />
                      </div>
                      {formData.avatar_url && (
                        <p className="text-xs text-gray-500">Current: {formData.avatar_url.substring(0, 50)}...</p>
                      )}
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Contact Information</h3>
                    <Input 
                      name="phone" 
                      placeholder="Phone Number" 
                      value={formData.phone} 
                      onChange={handleChange}
                    />
                    <p className="text-xs text-gray-500">
                      ℹ️ Your email is registered and cannot be changed here. For business details, social links, and branding, please add a business in the "My Businesses" section below.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-700">Save Changes</Button>
                    <Button type="button" onClick={() => setIsEditing(false)} variant="outline" className="flex-1">Cancel</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* About Section */}
          {!isEditing && user.bio && (
            <Card className="glass">
              <CardHeader>
                <CardTitle>About</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{user.bio}</p>
              </CardContent>
            </Card>
          )}

          {/* Featured Post */}
          {!isEditing && user.featured_post && (
            <Card className="glass border-l-4 border-rose-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-rose-600" />
                  Featured Achievement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{user.featured_post}</p>
              </CardContent>
            </Card>
          )}

          {/* Testimonials Section */}
          {!isEditing && (
            <Card className="glass">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Testimonials</CardTitle>
                  {!isOwnProfile && (
                    <Button onClick={() => setShowTestimonialForm(!showTestimonialForm)} variant="outline" size="sm">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Give Testimonial
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Pending Testimonials (Own Profile Only) */}
                {isOwnProfile && pendingTestimonials.length > 0 && (
                  <div className="space-y-3 mb-6">
                    <h3 className="font-semibold text-sm text-gray-600">Pending Approval</h3>
                    {pendingTestimonials.map((t) => (
                      <Card key={t.id} className="bg-yellow-50 border-yellow-200">
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm text-gray-700 mb-2">"{t.message}"</p>
                              <p className="text-xs text-gray-600">
                                - {t.from_user_name}
                                {t.relationship && ` (${t.relationship})`}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleApproveTestimonial(t.id, true)} className="bg-green-600 hover:bg-green-700">
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleApproveTestimonial(t.id, false)}>
                                Reject
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Testimonial Form */}
                {showTestimonialForm && !isOwnProfile && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-4 space-y-3">
                      <Textarea
                        placeholder="Share your experience working with this member..."
                        value={testimonialMessage}
                        onChange={(e) => setTestimonialMessage(e.target.value)}
                        rows={3}
                      />
                      <select
                        value={testimonialRelationship}
                        onChange={(e) => setTestimonialRelationship(e.target.value)}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select relationship (optional)</option>
                        <option value="colleague">Colleague</option>
                        <option value="client">Client</option>
                        <option value="partner">Partner</option>
                        <option value="mentor">Mentor</option>
                      </select>
                      <div className="flex gap-2">
                        <Button onClick={handleSubmitTestimonial} className="bg-rose-600 hover:bg-rose-700">
                          Submit Testimonial
                        </Button>
                        <Button variant="outline" onClick={() => setShowTestimonialForm(false)}>
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Approved Testimonials */}
                {testimonials.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No testimonials yet</p>
                ) : (
                  testimonials.map((t) => (
                    <Card key={t.id} className="bg-white">
                      <CardContent className="pt-4">
                        <p className="text-gray-700 italic mb-3">"{t.message}"</p>
                        <p className="text-sm text-gray-600">
                          - <span className="font-medium">{t.from_user_name}</span>
                          {t.relationship && <span className="text-gray-500"> ({t.relationship})</span>}
                        </p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          {/* Business Management Section (Own Profile Only) */}
          {!isEditing && isOwnProfile && (
            <Card className="glass">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-rose-600" />
                      My Businesses
                    </CardTitle>
                    <CardDescription>Manage your business showcase</CardDescription>
                  </div>
                  <Button 
                    onClick={() => handleBusinessDialogOpen()} 
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Business
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {businesses.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No businesses added yet</p>
                    <p className="text-sm text-gray-400 mb-4">Showcase your business to the community</p>
                    <Button 
                      onClick={() => handleBusinessDialogOpen()}
                      variant="outline"
                      className="text-rose-600 border-rose-300"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Business
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {businesses.map((business) => (
                      <Card key={business.id} className="bg-white hover:shadow-lg transition-all">
                        <CardContent className="pt-6">
                          {business.logo && (
                            <div className="w-full h-24 mb-4 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                              <img src={business.logo} alt={business.name} className="max-w-full max-h-full object-contain" />
                            </div>
                          )}
                          <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                            {business.name}
                          </h3>
                          <Badge variant="outline" className="mb-3 text-xs">{business.industry}</Badge>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{business.description}</p>
                          
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => handleBusinessDialogOpen(business)}
                              variant="outline"
                              size="sm"
                              className="flex-1"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            {business.website && (
                              <Button
                                onClick={() => window.open(business.website, '_blank')}
                                variant="outline"
                                size="sm"
                                className="flex-1"
                              >
                                <ExternalLink className="w-3 h-3 mr-1" />
                                Visit
                              </Button>
                            )}
                            <Button
                              onClick={() => handleBusinessDelete(business.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Business Dialog */}
          <Dialog open={businessDialogOpen} onOpenChange={setBusinessDialogOpen}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {editingBusiness ? 'Edit Business' : 'Add New Business'}
                </DialogTitle>
                <DialogDescription>
                  {editingBusiness ? 'Update your business information' : 'Showcase your business to the community'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleBusinessSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700">Basic Information</h3>
                  <Input
                    name="name"
                    placeholder="Business Name *"
                    value={businessFormData.name}
                    onChange={handleBusinessChange}
                    required
                  />
                  <Textarea
                    name="description"
                    placeholder="Description *"
                    value={businessFormData.description}
                    onChange={handleBusinessChange}
                    required
                    rows={4}
                  />
                  <Input
                    name="industry"
                    placeholder="Industry *"
                    value={businessFormData.industry}
                    onChange={handleBusinessChange}
                    required
                  />
                </div>

                {/* Image & Branding */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700">Image & Branding</h3>
                  <Input
                    name="logo"
                    placeholder="Logo URL"
                    value={businessFormData.logo}
                    onChange={handleBusinessChange}
                  />
                  <Input
                    name="cover_image"
                    placeholder="Cover/Banner Image URL"
                    value={businessFormData.cover_image}
                    onChange={handleBusinessChange}
                  />
                </div>

                {/* Quick Facts */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700">Quick Facts</h3>
                  <Input
                    name="location"
                    placeholder="Location (e.g., London, UK)"
                    value={businessFormData.location}
                    onChange={handleBusinessChange}
                  />
                  <select
                    name="team_size"
                    value={businessFormData.team_size}
                    onChange={handleBusinessChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Team Size</option>
                    <option value="1">Just Me</option>
                    <option value="2-10">2-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-200">51-200</option>
                    <option value="201+">201+</option>
                  </select>
                  <select
                    name="business_stage"
                    value={businessFormData.business_stage}
                    onChange={handleBusinessChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Business Stage</option>
                    <option value="idea">Idea Stage</option>
                    <option value="startup">Startup</option>
                    <option value="growing">Growing</option>
                    <option value="established">Established</option>
                  </select>
                </div>

                {/* Contact & Social */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-gray-700">Contact & Social Links</h3>
                  <Input
                    name="website"
                    placeholder="Website URL"
                    value={businessFormData.website}
                    onChange={handleBusinessChange}
                  />
                  <Input
                    name="linkedin_url"
                    placeholder="LinkedIn Profile/Page URL"
                    value={businessFormData.linkedin_url}
                    onChange={handleBusinessChange}
                  />
                  <Input
                    name="instagram_url"
                    placeholder="Instagram Profile URL"
                    value={businessFormData.instagram_url}
                    onChange={handleBusinessChange}
                  />
                  <Input
                    name="twitter_url"
                    placeholder="Twitter/X Profile URL"
                    value={businessFormData.twitter_url}
                    onChange={handleBusinessChange}
                  />
                  <Input
                    name="phone"
                    placeholder="Contact Phone Number"
                    value={businessFormData.phone}
                    onChange={handleBusinessChange}
                  />
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-700">
                    {editingBusiness ? 'Update Business' : 'Add Business'}
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => setBusinessDialogOpen(false)} 
                    variant="outline" 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Password Change Dialog */}
          <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Change Password</DialogTitle>
                <DialogDescription>
                  Enter your current password and choose a new password (minimum 12 characters with uppercase, lowercase, number, and special character)
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Current Password</label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Enter current password"
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">New Password</label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Enter new password (min. 12 characters)"
                    minLength={12}
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Confirm New Password</label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Re-enter new password"
                    minLength={8}
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1 bg-rose-600 hover:bg-rose-700">
                    Change Password
                  </Button>
                  <Button 
                    type="button" 
                    onClick={() => {
                      setShowPasswordDialog(false);
                      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                    }} 
                    variant="outline" 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;